import { isUndefined } from 'ply-utils'
import type { Cache } from './cache'
import type { Adapters } from './types'

const defaultNormalizer = (x: unknown) => x

export class Resolver {
	private cache: Cache
	private adapters: Adapters | null = null

	constructor({ cache }: { cache: Cache }) {
		this.cache = cache
	}

	setAdapters = (adapters: Adapters | null) => {
		if (adapters !== this.adapters) {
			this.adapters = adapters
		}
	}

	resolve = <Result>(...keys: string[]): Result => {
		return keys.map((key) => {
			if (this.cache.has(key)) {
				return this.cache.get(key)
			}

			const resolved = this.resolveKey(key)

			if (resolved === null) {
				console.warn(`key '${key}' resolved as null`)
			}

			return resolved
		}) as Result
	}

	resolveKey(key: string) {
		const allRequiredKeys = [key].reduce<string[]>(
			// biome-ignore lint/performance/noAccumulatingSpread: <explanation>
			(acc, key) => [...acc, key, ...this.getDependencyKeys(key)],
			[],
		)

		this.process(allRequiredKeys)

		return this.cache.get(key)
	}

	async process(keys: string[]) {
		// biome-ignore lint/complexity/noForEach: <explanation>
		keys.forEach((key) => this.getNormalizedValue(key))
	}

	getNormalizedValue(key: string) {
		if (this.cache.has(key)) {
			return this.cache.get(key)
		}

		const adapter = this.adapters?.get(key)

		if (!adapter) {
			const value = this.cache.getRaw(key)

			this.cache.set(key, value)

			return value
		}

		const { normalizer = defaultNormalizer, depends = [] } = adapter

		const dependValues = depends.map((key) => this.getNormalizedValue(key))
		const normalized = normalizer(this.cache.getRaw(key), dependValues)
		const resultValue = isUndefined(normalized) ? null : normalized

		this.cache.set(key, resultValue)

		return resultValue
	}

	getDependencyKeys(key: string): string[] {
		if (!this.adapters) {
			return []
		}

		const { depends = [] } = this.adapters.get(key) || {}

		const result = depends.reduce<string[]>(
			(acc, dependsConfigKey) => [
				// biome-ignore lint/performance/noAccumulatingSpread: <explanation>
				...acc,
				...this.getDependencyKeys(dependsConfigKey),
				dependsConfigKey,
			],
			[],
		)

		return result
	}
}
